import { usePathname, useSearchParams } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { DragStartEvent } from '@dnd-kit/core';
import { DragEndEvent } from '@dnd-kit/core/dist/types';
import { PortalServiceChangeEventDto } from '@nimbus/portal-service';
import { useToast } from '@nx-frontend/shared/ui/toast';
import {
  addFolderToPortal,
  addNoteToPortal,
  getPreparedPortalFolderMenuItem,
  getPreparedPortalNoteMenuItem,
} from '@nx-frontend/shared/actions';
import { postPortalContentClientApi } from '@nx-frontend/shared/api';
import { upsertWorkspaceNote } from '@nx-frontend/shared/api/webnotes';
import { getPortalContents } from '@nx-frontend/shared/api/webnotes/client';
import { calculateMenuItemPositionType, changeEventsToDto } from '@nx-frontend/shared/dtos';
import { useNavigateMenuItem } from '@nx-frontend/shared/hooks';
import { useChanges, useGetPreparedChangeEvent } from '@nx-frontend/shared/hooks/useChangeEvents';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  addMenuItemEvent,
  deleteMenuItemEvent,
  getIsMenuItemExpandedFx,
  getMenuItemByIdFx,
  getMenuItemIncludesFoldersFx,
  getMenuItemsByParentIdFx,
  getMenuRootOrderFx,
  getMenusExistedSlugsFx,
  getRootMenuDropAreaId,
  isFolderMenuItem,
  moveMenuItemEvent,
  toggleExpandItemFx,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { updateCurrentNoteEvent } from '@nx-frontend/shared/store/note';
import { addNoteToPortalEvent, removeNoteFromPortalEvent } from '@nx-frontend/shared/store/notes';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgWorkspaceAccess } from '@nx-frontend/shared/store/org-workspace-access';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $isCustomizerMode } from '@nx-frontend/shared/store/portal-app';
import { setSidebarDndActiveItemIdEvent } from '@nx-frontend/shared/store/sidebar-dnd';
import { $userId } from '@nx-frontend/shared/store/user';
import {
  ChangeEventType,
  MenuItem,
  MenuItemType,
  MenuType,
  PORTAL_MENU_TYPE,
  PortalMenuTypes,
  PortalServiceContentDto,
  PortalServiceMenuItemDtoPositionType,
  WorkspaceRole,
} from '@nx-frontend/shared/types';

interface CheckDropError {
  dragItem: MenuItem;
  dragItemParent?: MenuItem;
  dropItem?: MenuItem;
  dragMenuType: MenuType;
  dropMenuType: MenuType;

  dropItemParent?: MenuItem;

  isDropBefore: boolean;
  isDropEmpty: boolean;
}

enum DndDirection {
  PORTAL_TO_PORTAL = 'PORTAL_TO_PORTAL',
  PORTAL_TO_IS = 'PORTAL_TO_IS',
  IS_TO_IS = 'IS_TO_IS',
  IS_TO_PORTAL = 'IS_TO_PORTAL',
}

export function useSidebarDnd() {
  const [orgWorkspaceAccess, currentWorkspace, isCustomizer, portalSettings, userId, orgInfo] = useUnit([
    $orgWorkspaceAccess,
    $currentWorkspace,
    $isCustomizerMode,
    $portalSettings,
    $userId,
    $orgInfo,
  ]);
  const [
    setSidebarDndActiveItemId,
    getMenuItemById,
    getMenuRootOrder,
    getMenuItemIncludesFolders,
    moveMenuItem,
    deleteMenuItem,
    addMenuItem,
    removeNoteFromPortal,
    updateCurrentNote,
    getMenusExistedSlugs,
    moveNoteToPortal,
    getIsMenuItemExpanded,
    toggleExpandItem,
    getMenuItemsByParentId,
  ] = useEvent([
    setSidebarDndActiveItemIdEvent,
    getMenuItemByIdFx,
    getMenuRootOrderFx,
    getMenuItemIncludesFoldersFx,
    moveMenuItemEvent,
    deleteMenuItemEvent,
    addMenuItemEvent,
    removeNoteFromPortalEvent,
    updateCurrentNoteEvent,
    getMenusExistedSlugsFx,
    addNoteToPortalEvent,
    getIsMenuItemExpandedFx,
    toggleExpandItemFx,
    getMenuItemsByParentIdFx,
  ]);
  const setChanges = useChanges();
  const portalId = portalSettings.id;
  const workspaceId = currentWorkspace.globalId;
  const { toast } = useToast();
  const getPreparedChangeEvent = useGetPreparedChangeEvent();
  const navigateMenuItem = useNavigateMenuItem();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const hasAccess = () => {
    if (orgWorkspaceAccess) {
      const workspace = orgWorkspaceAccess.find((item) => item.workspaceId === currentWorkspace.globalId);
      if (workspace && workspace.access) {
        if (WorkspaceRole.READER === workspace.access.role) {
          return false;
        }
      }
    }
    return true;
  };

  const checkErrors = async ({
    dragItem,
    dropItem,
    dragItemParent,
    dropItemParent,
    dragMenuType,
    dropMenuType,
    isDropBefore,
    isDropEmpty,
  }: CheckDropError): Promise<string | null> => {
    const direction = getDndDirection(dragMenuType, dropMenuType);
    const dropParentId = getDropParentId(isDropEmpty, dropItem);
    const isDropInsideFolder = dropParentId.length;
    if (!hasAccess()) {
      return 'You can’t move items because you have “Read-only” access rights';
    }
    if (dragItem.type === MenuItemType.HOME) {
      return "You can't move the Home element";
    }
    if (dropItem?.type === MenuItemType.HOME && isDropBefore) {
      return "You can't drop items before the Home element";
    }
    if (dropMenuType === MenuType.PORTAL_TOPBAR && dragMenuType !== MenuType.PORTAL_TOPBAR) {
      const topBarRootOrder = await getMenuRootOrder({ menuType: MenuType.PORTAL_TOPBAR });
      if (isFolderMenuItem(dragItem.type)) {
        return "You can't drop folders in the top bar section";
      } else if (topBarRootOrder.length >= 5) {
        return "You've hit the limit! The top bar can hold up to 5 items";
      }
    }
    if (dropItemParent?.type === MenuItemType.NOTE || dropItemParent?.type === MenuItemType.PORTAL_PAGE) {
      return "You can't drop items on pages";
    }
    if (dropMenuType === MenuType.PORTAL_FOOTER && isFolderMenuItem(dragItem.type)) {
      const dragItemIncludesFolder = await getMenuItemIncludesFolders({ menuType: dragMenuType, id: dragItem.id });
      if (isDropInsideFolder || dragItemIncludesFolder) {
        return 'The footer folder does not support subfolders.';
      }
    }
    if (isDropInsideFolder) {
      if (dragItem.type === MenuItemType.FILES) {
        return "The file manager can't be moved to the folder";
      } else if (dragItem.type === MenuItemType.TASKS_DASHBOARD) {
        return "The task dashboard can't be moved to the folder";
      } else if (dragItem.type === MenuItemType.CHATS_DASHBOARD) {
        return "The chat dashboard can't be moved to the folder";
      } else if (dragItem.type === MenuItemType.PORTAL_PROCESS) {
        return "The process can't be moved to the folder";
      }
    }

    if (direction === DndDirection.IS_TO_IS) {
      if (dropParentId === dragItem.parentId) {
        return "Sorry, you can't change page sorting using dnd yet";
      }

      if (dragItem.type === MenuItemType.NOTE && dropParentId === 'notesFolder#root') {
        return "You can't drop a page to the root";
      }
    }

    if (dragItem.id === 'notesFolder#default') {
      return "You can't move default folder";
    }

    if (dropParentId === 'notesFolder#root' && dragItem.type !== MenuItemType.NOTES_FOLDER) {
      return "You can't drop item to the root";
    }

    if (direction === DndDirection.PORTAL_TO_IS) {
      if (
        dragItem.type === MenuItemType.PORTAL_SECTION ||
        dragItem.type === MenuItemType.PORTAL_PROCESS ||
        dragItem.type === MenuItemType.NOTES_FOLDER
      ) {
        return 'At the moment, only pages can be moved to the Internal Space.';
      }
      if (!dragItem.targetId || !dropParentId) {
        return "You can't move this item";
      }
    }

    if (dragItemParent?.originGlobalId && dragItem.parentId !== dropParentId) {
      return "You can't change projection parent";
    }

    if (dragItem.originGlobalId && dropParentId) {
      return 'You cannot move an linked item into a folder';
    }

    if (dragItem.parentId === dropParentId && !!dragItemParent?.originGlobalId) {
      return "You can't change order in projection";
    }

    if (isDropEmpty ? dropItem?.originGlobalId : dropItemParent?.originGlobalId) {
      return 'You cannot move an item into a linked folder';
    }

    return null;
  };

  const dragEnd = async (event: DragEndEvent) => {
    setSidebarDndActiveItemId(undefined);
    const dragId = event.active.data.current?.originalId;
    const dropId = event.over?.data.current?.originalId;
    const dragMenuType = event.active.data.current?.menuType;
    const dropMenuType = event.over?.data.current?.menuType;
    const isDropEmpty = event.over?.data.current?.isEmpty;
    const isDropBefore = event.over?.data.current?.isBefore;

    if (!(dragId && dropId && dragMenuType && dropMenuType && dragId !== dropId)) {
      return;
    }

    const dragItem = await getMenuItemById({ id: dragId, menuType: dragMenuType });
    const dropItem = await getMenuItemById({ id: dropId, menuType: dropMenuType });
    const dropItemParent: MenuItem | undefined = await getMenuItemById({
      id: dropItem?.parentId,
      menuType: dropMenuType,
    });

    const isDropInSamePosition = (): boolean => {
      return (
        dragMenuType === dropMenuType &&
        dragItem.parentId === dropItem.parentId &&
        ((isDropBefore && dropItem.index - 1 === dragItem.index) ||
          (!isDropBefore && dropItem.index + 1 === dragItem.index)) &&
        !isDropEmpty
      );
    };

    if (isDropInSamePosition()) {
      return;
    }

    const dragItemParent = dragItem.parentId
      ? await getMenuItemById({ id: dragItem.parentId, menuType: dragMenuType })
      : undefined;

    const errorMessage = await checkErrors({
      dragItem,
      dropItem,
      dragItemParent,
      dropItemParent,
      dragMenuType,
      dropMenuType,
      isDropBefore,
      isDropEmpty,
    });

    if (errorMessage) {
      toast({ title: errorMessage });
      return;
    }

    const dropIndex = getDropIndex({
      dragMenuType,
      dropMenuType,
      dragItem,
      dropItem,
      isDropBefore,
      isDropEmpty,
    });
    const dropParentId = getDropParentId(isDropEmpty, dropItem);

    const isRootDropArea = dropId === getRootMenuDropAreaId(dropMenuType);
    const direction = getDndDirection(dragMenuType, dropMenuType);

    if (direction === DndDirection.PORTAL_TO_PORTAL) {
      const oldValue = {
        id: dragItem.id,
        parentId: dragItem.parentId,
        menuType: dragMenuType,
        index: dragItem.index,
        oldMenuType: dropMenuType,
      };

      const newValue = isRootDropArea
        ? {
            id: dragItem.id,
            parentId: '',
            menuType: dropMenuType,
            index: 0,
            oldMenuType: dragMenuType,
          }
        : {
            id: dragItem.id,
            parentId: dropParentId,
            menuType: dropMenuType,
            index: dropIndex,
            oldMenuType: dragMenuType,
          };

      const changeEvent = getPreparedChangeEvent({
        type: ChangeEventType.MOVE_MENU_ITEM,
        oldValue,
        newValue,
      });

      if (isCustomizer) {
        setChanges(changeEvent);

        const path = pathname.split('/visual')[1];
        if (dragItem.parentId !== dropParentId && path === dragItem.url) {
          const item = await getMenuItemById({ id: dragId, menuType: dropMenuType });
          navigateMenuItem({ path: item.url, originalPath: item.originalUrl });
        } else if (dragItem.hasChildren || dragItem.order?.length) {
          if (path.startsWith(dragItem.url)) {
            const childrenItems = await getMenuItemsByParentId({ parentId: dragItem.id, menuType: dropMenuType });
            const originalUrl = searchParams.get('originalurl');
            const currentItem = childrenItems.find(
              (item) => item.originalUrl === path || (originalUrl && item.originalUrl === originalUrl)
            );
            if (currentItem) {
              navigateMenuItem({ path: currentItem.url, originalPath: currentItem.originalUrl });
            }
          }
        }
      } else {
        moveMenuItem({
          id: newValue.id,
          parentId: newValue.parentId,
          index: newValue.index,
          menuType: dropMenuType,
          oldMenuType: dragMenuType,
        });
        const changeEventsDto = changeEventsToDto([changeEvent]);
        const publishSuccess = await postPortalContentClientApi({
          workspaceId,
          portalId,
          changeEventsDto,
        })
          .then(() => true)
          .catch(() => false);
        if (!publishSuccess) {
          moveMenuItem({
            id: oldValue.id,
            parentId: oldValue.parentId,
            index: oldValue.index,
            menuType: dragMenuType,
            oldMenuType: dropMenuType,
          });
        }
      }
    } else if (direction === DndDirection.IS_TO_IS) {
      const newParentId = dropParentId === 'notesFolder#root' ? 'root' : dropParentId.split('#')[1];
      moveMenuItem({
        id: dragItem.id,
        parentId: dropParentId,
        index: dropIndex,
        menuType: dropMenuType,
        oldMenuType: dragMenuType,
      });

      const isUpdated = await upsertWorkspaceNote({
        workspaceId,
        noteId: dragItem.id.replace(dragItem.type + '#', ''),
        note: {
          parentId: newParentId,
        },
      });

      if (!isUpdated) {
        moveMenuItem({
          id: dragItem.id,
          parentId: dragItem.parentId,
          index: dragItem.index,
          menuType: dragMenuType,
          oldMenuType: dropMenuType,
        });
      }
    } else if (direction === DndDirection.PORTAL_TO_IS) {
      if (!dragItem.targetId) {
        return;
      }

      const targetParentId = dropParentId.split('#')[1];

      const updatedMenuItem = {
        ...dragItem,
        id: `note#${dragItem.targetId}`,
        index: dropIndex,
        parentId: dropParentId,
        positionType: '',
        is_portal_share: false,
        url: '',
        visible: true,
      };

      deleteMenuItem({
        menuType: dragMenuType,
        item: dragItem,
      });
      addMenuItem({ menuType: dropMenuType, item: updatedMenuItem });

      let portalContent: PortalServiceContentDto | null = null;
      try {
        portalContent = await getPortalContents({
          path: dragItem.url,
          portalDomain: portalSettings.domain,
        });
      } catch (err) {
        console.error(`can't get portal contents`, err);
      }
      const oldValue = Object.assign(
        {
          positionType: 'sidebar',
        } as { positionType: PortalServiceMenuItemDtoPositionType },
        dragItem
      );
      const changeEventsDto: PortalServiceChangeEventDto[] = [
        {
          type: 'deleteMenuItem',
          oldValue,
          newValue: undefined,
          meta: {
            userId: 0,
            pageId: portalContent?.page?.id ?? '',
          },
        } as PortalServiceChangeEventDto,
      ];
      try {
        await postPortalContentClientApi({
          workspaceId,
          portalId: portalSettings.id,
          changeEventsDto,
        });
        await upsertWorkspaceNote({
          workspaceId,
          noteId: dragItem.targetId,
          note: {
            parentId: targetParentId,
          },
        });
        if (removeNoteFromPortal) {
          removeNoteFromPortal({
            workspaceId,
            globalId: dragItem.targetId,
          });
        }
        if (updateCurrentNote) {
          updateCurrentNote({
            workspaceId,
            globalId: dragItem.targetId,
            isSharedForPortal: false,
          });
        }
      } catch (err) {
        deleteMenuItem({
          menuType: dropMenuType,
          item: updatedMenuItem,
        });
        addMenuItem({ menuType: dragMenuType, item: dragItem });
        return toast({ title: 'Sorry, there was an error moving the item' });
      }
    } else if (direction === DndDirection.IS_TO_PORTAL) {
      const existedSlugs = await getMenusExistedSlugs([
        MenuType.PORTAL_TOPBAR,
        MenuType.PORTAL_SIDEBAR,
        MenuType.PORTAL_FOOTER,
      ]);

      const positionType = calculateMenuItemPositionType(dropMenuType);
      if (positionType === 'unknown') {
        return;
      }

      if (dragItem.type === MenuItemType.NOTE) {
        const noteMenuItem = getPreparedPortalNoteMenuItem({
          noteId: dragId.replace('note#', ''),
          index: dropIndex,
          pageName: dragItem.name,
          pageVisibility: false,
          existedSlugs: existedSlugs,
          positionType: positionType,
          parentId: dropParentId,
          parentUrl: isRootDropArea ? undefined : isDropEmpty ? dropItem.url : dropItemParent?.url,
        });

        if (noteMenuItem !== null) {
          deleteMenuItem({
            menuType: MenuType.WEBNOTES_MENU,
            item: dragItem,
          });
          addMenuItem({ menuType: dropMenuType, item: noteMenuItem });
          const publishedSuccessfully = await addNoteToPortal({
            userId: userId,
            portalId: portalSettings.id,
            orgId: orgInfo.orgId,
            workspaceId: workspaceId,
            noteId: dragId.replace('note#', ''),
            menuItem: noteMenuItem,
          });

          if (!publishedSuccessfully) {
            deleteMenuItem({
              menuType: dropMenuType,
              item: noteMenuItem,
            });
            addMenuItem({ menuType: dragMenuType, item: dragItem });
            return toast({ title: 'Sorry, there was an error moving the item' });
          } else {
            moveNoteToPortal({
              workspaceId,
              globalId: dragId.replace('note#', ''),
              showMakeInvisibleNotification: true,
            });
          }
        } else {
          return toast({ title: 'Sorry, there was an error moving the item' });
        }
      } else if (dragItem.type === MenuItemType.NOTES_FOLDER) {
        const positionType = calculateMenuItemPositionType(dropMenuType);

        if (positionType === 'unknown') {
          return;
        }

        const notesFolderMenuItem = getPreparedPortalFolderMenuItem({
          noteId: dragId.replace('notesFolder#', ''),
          index: dropIndex,
          pageName: dragItem.name,
          pageVisibility: false,
          existedSlugs: existedSlugs,
          positionType,
          parentId: dropParentId,
          hasChildren: dragItem.hasChildren,
          parentUrl: dropItemParent?.url,
        });

        const children: MenuItem[] = [];
        const childItems = await getMenuItemsByParentId({ parentId: dragItem.id, menuType: dragMenuType });
        const toRemoveIds: Record<string, string> = {};
        if (dragItem.hasChildren && childItems.length) {
          const prepareChildItems = (originalParentId: string, newParentId: string, parentUrl: string) => {
            const childFolders = childItems.filter(
              (childItem) => childItem.type === MenuItemType.NOTES_FOLDER && childItem.parentId === originalParentId
            );
            const childNotes = childItems.filter(
              (childItem) => childItem.type !== MenuItemType.NOTES_FOLDER && childItem.parentId === originalParentId
            );

            for (const childNote of childNotes) {
              const preparedChildNoteMenuItem = getPreparedPortalNoteMenuItem({
                noteId: childNote.id.replace('note#', ''),
                index: childNote.index,
                pageName: childNote.name,
                pageVisibility: false,
                existedSlugs: existedSlugs,
                positionType,
                hasChildren: false,
                parentId: newParentId,
                parentUrl,
              });
              children.push(preparedChildNoteMenuItem);
              existedSlugs.push(preparedChildNoteMenuItem.slug);
              toRemoveIds[preparedChildNoteMenuItem.id] = childNote.id;
            }

            for (const folder of childFolders) {
              const preparedChildFolderMenuItem = getPreparedPortalFolderMenuItem({
                noteId: folder.id.replace('notesFolder#', ''),
                index: folder.index,
                pageName: folder.name,
                pageVisibility: false,
                existedSlugs: existedSlugs,
                positionType,
                hasChildren: folder.hasChildren,
                parentId: newParentId,
                parentUrl,
              });

              children.push(preparedChildFolderMenuItem);
              existedSlugs.push(preparedChildFolderMenuItem.slug);
              toRemoveIds[preparedChildFolderMenuItem.id] = folder.id;

              prepareChildItems(folder.id, preparedChildFolderMenuItem.id, preparedChildFolderMenuItem.url);
            }
          };

          prepareChildItems(dragItem.id, notesFolderMenuItem.id, notesFolderMenuItem.url);
        }

        if (notesFolderMenuItem !== null) {
          deleteMenuItem({
            menuType: MenuType.WEBNOTES_MENU,
            item: dragItem,
          });
          addMenuItem({ menuType: dropMenuType, item: notesFolderMenuItem });
          if (children.length) {
            children.forEach(async (child) => {
              const menuItemToDelete = await getMenuItemById({
                menuType: MenuType.WEBNOTES_MENU,
                id: toRemoveIds[child.id],
              });
              deleteMenuItem({
                menuType: MenuType.WEBNOTES_MENU,
                item: menuItemToDelete,
              });
              addMenuItem({ menuType: dropMenuType, item: child });
            });
          }
          const isFolderPublishedSuccesfully = await addFolderToPortal({
            userId: userId,
            portalId: portalSettings.id,
            orgId: orgInfo.orgId,
            workspaceId: workspaceId,
            noteId: dragId.replace('note#', ''),
            menuItem: notesFolderMenuItem,
            children,
          });
          if (!isFolderPublishedSuccesfully) {
            deleteMenuItem({
              menuType: dropMenuType,
              item: dragItem,
            });
            addMenuItem({ menuType: dragMenuType, item: dragItem });
            childItems.forEach(async (childItem) => {
              const removeId = Object.keys(toRemoveIds).find((id) => toRemoveIds[id] === childItem.id);
              if (removeId) {
                const removeMenuItem = await getMenuItemById({ menuType: MenuType.WEBNOTES_MENU, id: removeId });
                if (removeMenuItem) {
                  deleteMenuItem({
                    menuType: dropMenuType,
                    item: removeMenuItem,
                  });
                  addMenuItem({ menuType: dragMenuType, item: childItem });
                }
              }
            });
            return toast({ title: 'Sorry, there was an error moving the item' });
          } else {
            children.forEach((child) => {
              if (child.type === MenuItemType.NOTE) {
                moveNoteToPortal({
                  workspaceId,
                  globalId: child.targetId,
                  showMakeInvisibleNotification: true,
                });
              }
            });
          }
        } else {
          return toast({ title: 'Sorry, there was an error moving the item' });
        }
      }
    }
  };

  const dragStart = async (event: DragStartEvent) => {
    const id = event.active.data.current?.originalId;
    const menuType = event.active.data.current?.menuType;
    setSidebarDndActiveItemId(id);
    if (id && menuType) {
      const item = await getMenuItemById({ id, menuType });
      const isExpanded = await getIsMenuItemExpanded({ id });
      if (isExpanded) {
        toggleExpandItem({ item, menuType });
      }
    }
  };

  return {
    dragEnd,
    dragStart,
  };
}

function getDropParentId(isDropEmpty: boolean, dropItem?: MenuItem) {
  return dropItem ? (isDropEmpty ? dropItem.id : dropItem.parentId) : '';
}

function getDropIndex({
  dragMenuType,
  dropMenuType,
  dragItem,
  dropItem,
  isDropBefore,
  isDropEmpty,
}: {
  dragMenuType: MenuType;
  dropMenuType: MenuType;
  dragItem: MenuItem;
  dropItem: MenuItem;
  isDropBefore: boolean;
  isDropEmpty: boolean;
}) {
  if (isDropEmpty) {
    return 0;
  }
  if (dragMenuType === dropMenuType && dragItem.parentId === dropItem.parentId) {
    if (dragItem.index < dropItem.index) {
      if (isDropBefore) {
        return dropItem.index - 1;
      } else {
        return dropItem.index;
      }
    }
  }
  if (!isDropBefore) {
    return dropItem.index + 1;
  }
  return dropItem.index;
}

function getIsPortalMenu(menuType: MenuType): boolean {
  return PortalMenuTypes.includes(menuType as PORTAL_MENU_TYPE);
}

function getDndDirection(dragMenuType: MenuType, dropMenuType: MenuType): DndDirection {
  if (getIsPortalMenu(dragMenuType) && getIsPortalMenu(dropMenuType)) {
    return DndDirection.PORTAL_TO_PORTAL;
  }
  if (getIsPortalMenu(dragMenuType) && !getIsPortalMenu(dropMenuType)) {
    return DndDirection.PORTAL_TO_IS;
  }
  if (!getIsPortalMenu(dragMenuType) && getIsPortalMenu(dropMenuType)) {
    return DndDirection.IS_TO_PORTAL;
  }
  return DndDirection.IS_TO_IS;
}
